import React, { useCallback, useMemo, useState, lazy, Suspense } from 'react';
import moment from 'moment';
import { useSecurity } from '../../../../../../SecurityContext';
import { useDashboardHistoryAccess } from '../useDashboardHistoryAccess';
import { fetchEventByOccurrenceId } from '../../../calendarTools/shared/services/eventDataService';
import { formatUec } from '../../../calendarTools/shared/components/benefitsBlock/utils';
import LoadingSpinner from '../../../../../../components/LoadingSpinner';

const CalendarEvent = lazy(() => import('../../../calendarTools/CalendarEvent'));

function formatYmd(ts) {
  if (!ts) return '—';
  const m = moment(ts);
  if (!m.isValid()) return '—';
  return m.format('YYYY-MM-DD');
}

function buildBenefitsByOccurrence(creditEntries, lootEntries) {
  const creditsByOcc = new Map();
  const lootsByOcc = new Map();

  for (const entry of creditEntries) {
    const oid = Number(entry?.eventOccurrenceId);
    if (!Number.isInteger(oid) || oid < 1) continue;
    const prev = creditsByOcc.get(oid) || { amount: 0, currency: entry?.currency || 'UEC' };
    prev.amount += Number(entry?.amount) || 0;
    if (entry?.currency) prev.currency = entry.currency;
    creditsByOcc.set(oid, prev);
  }

  for (const entry of lootEntries) {
    const oid = Number(entry?.eventOccurrenceId);
    if (!Number.isInteger(oid) || oid < 1) continue;
    const list = lootsByOcc.get(oid) || [];
    list.push(entry);
    lootsByOcc.set(oid, list);
  }

  return { creditsByOcc, lootsByOcc };
}

function formatParticipationHandles(eventRow) {
  const participants = Array.isArray(eventRow?.participants) ? eventRow.participants : [];
  const handles = [...new Set(
    participants
      .map((p) => (p?.usedHandle ? String(p.usedHandle).trim() : null))
      .filter(Boolean),
  )];
  return handles.join(', ');
}

export default function MemberDashboardHistoryList({ player, events, creditEntries, lootEntries }) {
  const { userHasPermission } = useSecurity();
  const { canViewHistory } = useDashboardHistoryAccess(player);
  const canBypass = userHasPermission('private.navbar.roster.file.history.bypass', 'read');

  const [expandedOccIds, setExpandedOccIds] = useState(() => new Set());
  const [showEventModal, setShowEventModal] = useState(false);
  const [activeEventId, setActiveEventId] = useState(null);
  const [selectedOccurrence, setSelectedOccurrence] = useState(null);

  const { creditsByOcc, lootsByOcc } = useMemo(
    () => buildBenefitsByOccurrence(creditEntries, lootEntries),
    [creditEntries, lootEntries],
  );

  const toggleLootExpanded = useCallback((occurrenceId) => {
    const oid = Number(occurrenceId);
    if (!Number.isInteger(oid) || oid < 1) return;
    setExpandedOccIds((prev) => {
      const next = new Set(prev);
      if (next.has(oid)) next.delete(oid);
      else next.add(oid);
      return next;
    });
  }, []);

  const openOccurrence = useCallback(async (occurrenceId) => {
    const oid = Number(occurrenceId);
    if (!Number.isInteger(oid) || oid < 1) return;

    try {
      const eventData = await fetchEventByOccurrenceId(oid);
      const occ =
        Array.isArray(eventData?.occurrences)
          ? eventData.occurrences.find((o) => Number(o?.id) === oid) || eventData.occurrences[0]
          : null;

      if (!eventData?.id || !occ?.startTime) return;

      setActiveEventId(eventData.id);
      setSelectedOccurrence({
        occurrenceId: oid,
        startTime: new Date(occ.startTime),
        endTime: occ.endTime ? new Date(occ.endTime) : null,
      });
      setShowEventModal(true);
    } catch (e) {
      setShowEventModal(false);
    }
  }, []);

  const closeEventModal = useCallback(() => {
    setShowEventModal(false);
  }, []);

  if (!canViewHistory && !canBypass) {
    return (
      <p className="member-dashboard__panel-placeholder">
        <em>You cannot access the details of an event you did not attend.</em>
      </p>
    );
  }

  if (!events.length) {
    return <p className="member-dashboard__panel-placeholder">No occurrences found.</p>;
  }

  return (
    <>
      <div className="member-dashboard__history-scroll">
        <div className="member-dashboard__history">
          {events.map((eventRow, idx) => {
            const occId = eventRow?.occurrenceId ?? eventRow?.eventOccurrenceId ?? eventRow?.occurrence ?? null;
            const key = `${occId || 'occ'}-${idx}`;
            const isRedacted = eventRow?.redacted === true && !canBypass;
            const credit = creditsByOcc.get(Number(occId));
            const loots = lootsByOcc.get(Number(occId)) || [];
            const hasLoot = loots.length > 0;
            const lootExpanded = expandedOccIds.has(Number(occId));
            const participationHandles = formatParticipationHandles(eventRow);

            return (
              <div
                key={key}
                className={
                  'member-dashboard__history-row' +
                  (isRedacted ? ' member-dashboard__history-row--redacted' : '') +
                  (lootExpanded ? ' member-dashboard__history-row--expanded' : '')
                }
              >
                <div className="member-dashboard__history-row__date">
                  {isRedacted ? '—' : formatYmd(eventRow.startTime)}
                </div>

                {isRedacted ? (
                  <div className="member-dashboard__history-row__redacted">Redacted</div>
                ) : (
                  <button
                    type="button"
                    className="member-dashboard__history-row__link"
                    onClick={() => openOccurrence(occId)}
                  >
                    {eventRow.eventName || '—'}
                  </button>
                )}

                <div className="member-dashboard__history-row__income">
                  {isRedacted || !credit?.amount
                    ? '—'
                    : `${formatUec(credit.amount)} ${credit.currency || 'UEC'}`}
                </div>

                <div className="member-dashboard__history-row__loot">
                  {!isRedacted && hasLoot ? (
                    <button
                      type="button"
                      className={
                        'member-dashboard__history-loot-chip' +
                        (lootExpanded ? ' member-dashboard__history-loot-chip--active' : '')
                      }
                      aria-expanded={lootExpanded}
                      onClick={() => toggleLootExpanded(occId)}
                    >
                      loot
                    </button>
                  ) : null}
                </div>

                <div className="member-dashboard__history-row__handle">
                  {isRedacted ? '—' : participationHandles || '—'}
                </div>

                {!isRedacted && lootExpanded && hasLoot ? (
                  <ul className="member-dashboard__history-loot-details">
                    {loots.map((loot) => {
                      const label = loot?.itemLabel || loot?.itemCode || '—';
                      const qty = Number(loot?.quantity) || 1;
                      return (
                        <li key={loot.id ?? `${label}-${qty}`} className="member-dashboard__history-loot-item">
                          <span className="member-dashboard__history-loot-item__label">{label}</span>
                          {qty > 1 ? (
                            <span className="member-dashboard__history-loot-item__qty">×{qty}</span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <Suspense fallback={<LoadingSpinner />}>
        {showEventModal && activeEventId && selectedOccurrence ? (
          <CalendarEvent
            eventId={activeEventId}
            show={showEventModal}
            onClose={closeEventModal}
            selectedOccurrence={selectedOccurrence}
            clearBrowserUrlOnClose={false}
          />
        ) : null}
      </Suspense>
    </>
  );
}
